// ============================================
// themePrimitive.js - ENHANCED THEME
// ============================================

import { createTheme } from "@mui/material/styles";

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === "light"
      ? {
          // Light Mode Palette - Refined colors
          primary: {
            main: "#3b82f6", // Modern blue
            light: "#60a5fa",
            dark: "#2563eb",
            contrastText: "#ffffff",
          },
          secondary: {
            main: "#8b5cf6", // Purple accent
            light: "#a78bfa",
            dark: "#7c3aed",
          },
          success: {
            main: "#10b981",
            light: "#34d399",
            dark: "#059669",
          },
          error: {
            main: "#ef4444",
            light: "#f87171",
            dark: "#dc2626",
          },
          warning: {
            main: "#f59e0b",
            light: "#fbbf24",
            dark: "#d97706",
          },
          info: {
            main: "#06b6d4",
            light: "#22d3ee",
            dark: "#0891b2",
          },
          background: {
            default: "#f8fafc",
            paper: "#ffffff",
          },
          text: {
            primary: "#0f172a",
            secondary: "#64748b",
          },
          divider: "rgba(15, 23, 42, 0.12)",
        }
      : {
          // Dark Mode Palette - Better contrast
          primary: {
            main: "#60a5fa",
            light: "#93c5fd",
            dark: "#3b82f6",
            contrastText: "#0f172a",
          },
          secondary: {
            main: "#a78bfa",
            light: "#c4b5fd",
            dark: "#8b5cf6",
          },
          success: {
            main: "#34d399",
            light: "#6ee7b7",
            dark: "#10b981",
          },
          error: {
            main: "#f87171",
            light: "#fca5a5",
            dark: "#ef4444",
          },
          warning: {
            main: "#fbbf24",
            light: "#fcd34d",
            dark: "#f59e0b",
          },
          info: {
            main: "#22d3ee",
            light: "#67e8f9",
            dark: "#06b6d4",
          },
          background: {
            default: "#0f172a",
            paper: "#1e293b",
          },
          text: {
            primary: "#f1f5f9",
            secondary: "#cbd5e1",
          },
          divider: "rgba(241, 245, 249, 0.12)",
        }),
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700, letterSpacing: "-0.02em" },
    h2: { fontWeight: 700, letterSpacing: "-0.01em" },
    h3: { fontWeight: 600, letterSpacing: "-0.01em" },
    h4: { fontWeight: 600, letterSpacing: "-0.01em" },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: {
      textTransform: "none",
      fontWeight: 600,
      letterSpacing: "0.01em",
    },
  },
  shadows: [
    "none",

    // 1 — Very soft
    mode === "light"
      ? "0 1px 2px rgba(0,0,0,0.06)"
      : "0 1px 2px rgba(0,0,0,0.25)",

    // 2
    mode === "light"
      ? "0 2px 4px rgba(0,0,0,0.08)"
      : "0 2px 4px rgba(0,0,0,0.30)",

    // 3
    mode === "light"
      ? "0 3px 6px rgba(0,0,0,0.10)"
      : "0 3px 6px rgba(0,0,0,0.35)",

    // 4
    mode === "light"
      ? "0 4px 8px rgba(0,0,0,0.12)"
      : "0 4px 8px rgba(0,0,0,0.40)",

    // 5
    mode === "light"
      ? "0 6px 12px rgba(0,0,0,0.14)"
      : "0 6px 12px rgba(0,0,0,0.45)",

    // Keep rest VERY subtle for higher elevations
    ...Array(19).fill(
      mode === "light"
        ? "0 8px 16px rgba(0,0,0,0.12)"
        : "0 8px 16px rgba(0,0,0,0.35)"
    ),
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: "thin",
          scrollbarColor:
            mode === "light" ? "#cbd5e1 #f1f5f9" : "#475569 #1e293b",
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: mode === "light" ? "#f1f5f9" : "#1e293b",
          },
          "&::-webkit-scrollbar-thumb": {
            background: mode === "light" ? "#cbd5e1" : "#475569",
            borderRadius: "4px",
            "&:hover": {
              background: mode === "light" ? "#94a3b8" : "#64748b",
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
          padding: "8px 20px",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow:
              mode === "light"
                ? "0 4px 12px rgba(0,0,0,0.15)"
                : "0 4px 12px rgba(0,0,0,0.4)",
          },
        },
        outlined: {
          borderWidth: "1.5px",
          "&:hover": {
            borderWidth: "1.5px",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          // COMPREHENSIVE AUTOFILL FIX FOR ALL TEXTFIELDS
          "& input:-webkit-autofill": {
            WebkitBoxShadow:
              mode === "light"
                ? "0 0 0 100px #ffffff inset !important"
                : "0 0 0 100px #1e293b inset !important",
            WebkitTextFillColor:
              mode === "light" ? "#0f172a !important" : "#f1f5f9 !important",
            caretColor: mode === "light" ? "#0f172a" : "#f1f5f9",
            borderRadius: "inherit",
          },
          "& input:-webkit-autofill:hover": {
            WebkitBoxShadow:
              mode === "light"
                ? "0 0 0 100px #ffffff inset !important"
                : "0 0 0 100px #1e293b inset !important",
          },
          "& input:-webkit-autofill:focus": {
            WebkitBoxShadow:
              mode === "light"
                ? "0 0 0 100px #ffffff inset !important"
                : "0 0 0 100px #1e293b inset !important",
          },
          "& input:-webkit-autofill:active": {
            WebkitBoxShadow:
              mode === "light"
                ? "0 0 0 100px #ffffff inset !important"
                : "0 0 0 100px #1e293b inset !important",
          },
        },
      },
      defaultProps: {
        InputLabelProps: {
          shrink: true,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
          transition: "all 0.2s ease",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: mode === "light" ? "#94a3b8" : "#64748b",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: "2px",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        },
        rounded: {
          borderRadius: "16px",
        },
        elevation1: {
          boxShadow:
            mode === "light"
              ? "0px 2px 8px rgba(0, 0, 0, 0.08)"
              : "0px 2px 8px rgba(0, 0, 0, 0.4)",
        },
        elevation2: {
          boxShadow:
            mode === "light"
              ? "0px 4px 16px rgba(0, 0, 0, 0.1)"
              : "0px 4px 16px rgba(0, 0, 0, 0.5)",
        },
        elevation3: {
          boxShadow:
            mode === "light"
              ? "0px 8px 24px rgba(0, 0, 0, 0.12)"
              : "0px 8px 24px rgba(0, 0, 0, 0.6)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
          border: `1px solid ${
            mode === "light"
              ? "rgba(15, 23, 42, 0.08)"
              : "rgba(241, 245, 249, 0.08)"
          }`,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow:
              mode === "light"
                ? "0 12px 32px rgba(0,0,0,0.12)"
                : "0 12px 32px rgba(0,0,0,0.6)",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor:
            mode === "light"
              ? "rgba(255, 255, 255, 0.85)"
              : "rgba(15, 23, 42, 0.85)",
          backdropFilter: "blur(20px)",
          boxShadow: "none",
          borderBottom: `1px solid ${
            mode === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)"
          }`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor:
              mode === "light"
                ? "rgba(59, 130, 246, 0.04)"
                : "rgba(96, 165, 250, 0.08)",
            cursor: "pointer",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "6px 12px",
          borderBottom: `1px solid ${
            mode === "light"
              ? "rgba(15, 23, 42, 0.08)"
              : "rgba(241, 245, 249, 0.08)"
          }`,
        },
        head: {
          fontWeight: 600,
          backgroundColor:
            mode === "light"
              ? "rgba(248, 250, 252, 0.8)"
              : "rgba(30, 41, 59, 0.8)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          fontWeight: 500,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: mode === "light" ? "#0f172a" : "#f1f5f9",
          color: mode === "light" ? "#f1f5f9" : "#0f172a",
          fontSize: "0.75rem",
          borderRadius: "8px",
          padding: "8px 12px",
        },
        arrow: {
          color: mode === "light" ? "#0f172a" : "#f1f5f9",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor:
              mode === "light"
                ? "rgba(59, 130, 246, 0.08)"
                : "rgba(96, 165, 250, 0.12)",
          },
        },
      },
    },
  },
});

export const createAppTheme = (mode) => {
  const theme = createTheme(getDesignTokens(mode));
  return theme;
};
